import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Loader2, Sparkles, CreditCard, AlertTriangle, Package, Plus, Upload, X, FileText, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { TICKET_CATEGORY_OPTIONS, URGENCY_OPTIONS, CATEGORY_DESCRIPTIONS, formatCurrency } from '../lib/constants';

const CreateTicketPage = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    category: '',
    title: '',
    description: '',
    urgency: 'sedang',
    estimated_cost: 0,
    location_id: user?.location_id || '',
    attachments: [],
  });
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]); // [{item_id, qty}]
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [locsRes, itemsRes, stocksRes] = await Promise.all([
        api.get('/locations'),
        api.get('/items', { params: { include_all: true } }),
        api.get('/warehouse/stock'),
      ]);
      setLocations(locsRes.data.filter((loc) => !loc.is_warehouse));
      setItems(itemsRes.data);
      setStocks(stocksRes.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getStockAt = (itemId, locationId) => {
    const stock = stocks.find((s) => s.item_id === itemId && s.location_id === locationId);
    return stock?.quantity || 0;
  };

  const addItemToList = () => {
    setSelectedItems([...selectedItems, { item_id: '', qty: 1 }]);
  };

  const removeItemFromList = (idx) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== idx));
  };

  const updateItemInList = (idx, field, value) => {
    const updated = [...selectedItems];
    updated[idx] = { ...updated[idx], [field]: value };
    setSelectedItems(updated);
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (field === 'category' || field === 'title' || field === 'description') {
      setAiResult(null); // Reset AI estimate if category/desc changes
    }
  };

  const handleEstimateAI = async () => {
    if (!formData.title || !formData.description || !formData.category) {
      toast.error('Isi kategori, judul, dan deskripsi terlebih dahulu');
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post('/tickets/estimate-cost', {
        category: formData.category,
        title: formData.title,
        description: formData.description,
      });
      setAiResult(data);
      setFormData({ ...formData, estimated_cost: data.estimated_cost });
      toast.success('Estimasi AI berhasil');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI estimasi gagal');
    } finally {
      setAiLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingFile(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/uploads/receipt', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setFormData((f) => ({ ...f, attachments: [...f.attachments, data] }));
      }
      toast.success(`${files.length} file berhasil diupload`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal upload file');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (idx) => {
    setFormData((f) => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/tickets', {
        ...formData,
        estimated_cost: parseFloat(formData.estimated_cost) || 0,
        attachments: formData.attachments.map((a) => a.url),
      });
      
      // Allocate selected items to the newly created ticket
      const validItems = selectedItems.filter((si) => si.item_id && parseInt(si.qty) > 0);
      for (const si of validItems) {
        try {
          await api.post(`/tickets/${data.ticket_number}/allocate-item`, {
            item_id: si.item_id,
            quantity: parseInt(si.qty),
            from_location: 'PUSAT',
          });
        } catch (err) {
          const itemName = items.find((i) => i.id === si.item_id)?.name || 'Barang';
          toast.error(`Gagal alokasi ${itemName}: ${err.response?.data?.detail || 'error'}`);
        }
      }
      
      if (validItems.length > 0) {
        toast.success(`Tiket dibuat + ${validItems.length} barang dialokasi`);
      } else {
        toast.success('Tiket berhasil dibuat');
      }
      navigate(`/tickets/${data.ticket_number}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat tiket');
    } finally {
      setLoading(false);
    }
  };

  const needsLocationSelector = !user?.location_id;
  const isPettyCash = formData.category === 'petty_cash';
  const selectedLocation = locations.find((l) => l.id === formData.location_id);

  const confidenceColor = {
    low: 'bg-amber-100 text-amber-700 border-amber-300',
    medium: 'bg-blue-100 text-blue-700 border-blue-300',
    high: 'bg-green-100 text-green-700 border-green-300',
  };

  return (
    <Layout>
      <div className="max-w-3xl space-y-6" data-testid="create-ticket-page">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/tickets')}
            className="mb-4"
            data-testid="back-to-tickets-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke Daftar Tiket
          </Button>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Buat Tiket</p>
          <h1 className="text-4xl font-medium tracking-tight">Tiket Baru</h1>
          <p className="text-muted-foreground mt-1">Isi form di bawah untuk membuat tiket permintaan baru</p>
        </div>

        <Card className="border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-xl tracking-tight font-medium">Informasi Tiket</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {needsLocationSelector && (
                <div className="space-y-2">
                  <Label htmlFor="location">Lokasi Cabang *</Label>
                  <Select
                    value={formData.location_id}
                    onValueChange={(v) => handleChange('location_id', v)}
                    required
                  >
                    <SelectTrigger data-testid="ticket-location-select">
                      <SelectValue placeholder="Pilih lokasi cabang" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Kategori Tiket *</Label>
                  <Select value={formData.category} onValueChange={(v) => handleChange('category', v)} required>
                    <SelectTrigger data-testid="ticket-category-select">
                      <SelectValue placeholder="Pilih kategori" />
                    </SelectTrigger>
                    <SelectContent>
                      {TICKET_CATEGORY_OPTIONS.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.category && (
                    <p className="text-xs text-muted-foreground">
                      {CATEGORY_DESCRIPTIONS[formData.category]}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="urgency">Tingkat Urgensi *</Label>
                  <Select value={formData.urgency} onValueChange={(v) => handleChange('urgency', v)} required>
                    <SelectTrigger data-testid="ticket-urgency-select">
                      <SelectValue placeholder="Pilih urgensi" />
                    </SelectTrigger>
                    <SelectContent>
                      {URGENCY_OPTIONS.map((u) => (
                        <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Bank info display for Petty Cash / Refund / Utility / Transfer */}
              {['petty_cash', 'refund_deposit', 'utility_payment', 'transfer_operasional'].includes(formData.category) && selectedLocation && (
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-primary" />
                    <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">
                      Rekening Tujuan {isPettyCash ? 'Petty Cash' : 'Pembayaran'}
                    </p>
                  </div>
                  {selectedLocation.bank_name ? (
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{selectedLocation.bank_name}</p>
                      <p className="text-sm font-mono">{selectedLocation.account_number}</p>
                      <p className="text-xs text-muted-foreground">a.n. {selectedLocation.account_holder}</p>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">
                        Cabang <strong>{selectedLocation.name}</strong> belum punya info rekening.
                        Hubungi Super Admin untuk melengkapi info bank di menu <em>Lokasi Cabang</em>.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="title">Judul Tiket *</Label>
                <Input
                  id="title"
                  placeholder="Contoh: AC di lobby tidak dingin"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  required
                  data-testid="ticket-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Deskripsi Detail *</Label>
                <Textarea
                  id="description"
                  placeholder="Jelaskan detail masalah, jenis barang/jasa yang dibutuhkan, dan lokasi spesifik..."
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  required
                  rows={6}
                  data-testid="ticket-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Lampiran (opsional)</Label>
                <p className="text-xs text-muted-foreground">Foto atau dokumen pendukung — PDF, JPG, PNG, WebP. Maks 8MB per file.</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="ticket-attachment-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile}
                  data-testid="ticket-attachment-upload-button"
                >
                  {uploadingFile ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Mengupload...</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Upload File</>
                  )}
                </Button>
                {formData.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.attachments.map((att, idx) => {
                      const isImage = (att.mime || '').startsWith('image/');
                      return (
                        <div
                          key={idx}
                          className="flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-lg border border-border bg-secondary/40 text-sm"
                          data-testid={`ticket-attachment-item-${idx}`}
                        >
                          {isImage ? <ImageIcon className="w-4 h-4 text-muted-foreground" /> : <FileText className="w-4 h-4 text-muted-foreground" />}
                          <span className="max-w-[160px] truncate">{att.filename}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveAttachment(idx)}
                            className="p-0.5 rounded hover:bg-destructive/10 hover:text-destructive"
                            data-testid={`ticket-attachment-remove-${idx}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Shopping List - only for umum/it/me */}
              {['umum', 'it', 'me'].includes(formData.category) && (
                <div className="space-y-3 p-4 rounded-lg border border-primary/20 bg-primary/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold flex items-center gap-2">
                        <Package className="w-4 h-4 text-primary" />
                        Barang yang Diperlukan (opsional)
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Barang diambil dari Gudang Pusat. Otomatis dialokasi ke tiket saat submit.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addItemToList}
                      data-testid="add-shopping-item-button"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Tambah
                    </Button>
                  </div>

                  {selectedItems.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">
                      Belum ada barang. Klik "Tambah" jika perlu barang dari gudang.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {selectedItems.map((si, idx) => {
                        const stock = si.item_id ? getStockAt(si.item_id, 'PUSAT') : 0;
                        return (
                          <div key={idx} className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                            <Select
                              value={si.item_id}
                              onValueChange={(v) => updateItemInList(idx, 'item_id', v)}
                            >
                              <SelectTrigger className="flex-1 min-w-0" data-testid={`shopping-item-select-${idx}`}>
                                <SelectValue placeholder="Pilih barang" />
                              </SelectTrigger>
                              <SelectContent>
                                {items.map((it) => {
                                  const s = getStockAt(it.id, 'PUSAT');
                                  return (
                                    <SelectItem key={it.id} value={it.id} disabled={s === 0}>
                                      {it.name} ({it.unit})
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                            <Input
                              type="number"
                              min="1"
                              max={stock}
                              value={si.qty}
                              onChange={(e) => updateItemInList(idx, 'qty', e.target.value)}
                              className="w-20"
                              data-testid={`shopping-qty-${idx}`}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItemFromList(idx)}
                              className="text-destructive"
                              data-testid={`remove-shopping-${idx}`}
                            >
                              ✕
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="estimated_cost">Estimasi Biaya (Rp)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleEstimateAI}
                    disabled={aiLoading || !formData.category || !formData.title || !formData.description}
                    data-testid="ai-estimate-button"
                  >
                    {aiLoading ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                        Estimasi...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3 mr-2 text-primary" />
                        Estimasi dengan AI
                      </>
                    )}
                  </Button>
                </div>
                <Input
                  id="estimated_cost"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={formData.estimated_cost}
                  onChange={(e) => handleChange('estimated_cost', e.target.value)}
                  data-testid="ticket-estimated-cost-input"
                />
                <p className="text-xs text-muted-foreground">Klik "Estimasi dengan AI" untuk mendapat estimasi otomatis dari harga pasar Indonesia</p>
              </div>

              {/* AI Estimate Result */}
              {aiResult && (
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 space-y-3" data-testid="ai-result">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      <p className="text-sm font-semibold">Estimasi AI</p>
                    </div>
                    <Badge variant="outline" className={confidenceColor[aiResult.confidence] || confidenceColor.medium}>
                      Confidence: {aiResult.confidence}
                    </Badge>
                  </div>
                  <p className="text-2xl font-medium tracking-tight text-primary">
                    {formatCurrency(aiResult.estimated_cost)}
                  </p>
                  {aiResult.reasoning && (
                    <p className="text-sm text-muted-foreground italic">"{aiResult.reasoning}"</p>
                  )}
                  {aiResult.breakdown && aiResult.breakdown.length > 0 && (
                    <div className="pt-3 border-t border-primary/20">
                      <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">Rincian</p>
                      <div className="space-y-1">
                        {aiResult.breakdown.map((b, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-foreground">
                              {b.item} <span className="text-muted-foreground">× {b.qty}</span>
                            </span>
                            <span className="font-medium">{formatCurrency(b.subtotal)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground pt-2 border-t border-primary/20">
                    * Estimasi berdasarkan harga pasar Indonesia. Bisa Anda ubah manual di kolom estimasi biaya.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3 pt-4 border-t border-border">
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="submit-ticket-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Menyimpan...
                    </>
                  ) : (
                    'Buat Tiket'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/tickets')}
                  data-testid="cancel-ticket-button"
                >
                  Batal
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default CreateTicketPage;
